import React, {ReactNode, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Navigate} from "react-router-dom";
import {ErrorBoundary, FallbackProps} from "react-error-boundary";
import Flashbar from "@awsui/components-react/flashbar";
import UnauthorizedError from "api/errors/UnauthorizedError";
import {useAuth} from "hooks/useAuth";


interface UnauthorizedContainerProps {
    children: ReactNode;
}

const UnauthorizedContainer: React.FC<UnauthorizedContainerProps> = ({children}) => {

    const {t} = useTranslation("common");
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [{isSignedIn}] = useAuth();

    // If an UnauthorizedError is thrown while the user was not signed in (e.g. a stale
    // page with an expired token), reload once to flush in-memory state. Doing this
    // inside useEffect instead of during render keeps the render function pure and
    // avoids infinite re-render loops.
    useEffect(() => {
        if (isUnauthorized && !isSignedIn) {
            window.location.reload();
        }
    }, [isUnauthorized, isSignedIn]);

    const UnauthorizedFallback: React.FC<FallbackProps> = () => (
        <Flashbar items={[{
            type: "info",
            header: t("errors.reloadHint"),
            content: "",
            dismissible: false
        }]}/>
    );

    const handleError = (error: Error): void => {
        if (error instanceof UnauthorizedError) {
            setIsUnauthorized(true);
        }
    };

    if (isUnauthorized && isSignedIn) {
        return (
            <Navigate to={"/signout"}/>
        );
    }

    return (
        <ErrorBoundary FallbackComponent={UnauthorizedFallback} onError={handleError}>
            {children}
        </ErrorBoundary>
    );
};

export default UnauthorizedContainer;
